import React, { useState, useEffect, useRef, useCallback } from 'react';

const tracks = [
  '/audio/track1.mp3',
  '/audio/track2.mp3'
];

export default function BackgroundMusic() {
  const [currentTrack, setCurrentTrack] = useState(0);
  const [muted, setMuted] = useState(() => localStorage.getItem('velvet_music_muted') === 'true');
  const audioRef = useRef(null);
  const hasStartedRef = useRef(false);

  // Start music on first user interaction
  const startMusic = useCallback(() => {
    if (hasStartedRef.current) return;
    
    if (audioRef.current) {
      audioRef.current.volume = 0.5;
      if (localStorage.getItem('velvet_music_muted') !== 'true') {
        audioRef.current.play()
          .then(() => { hasStartedRef.current = true; })
          .catch((err) => { console.warn('Audio autoplay blocked:', err); });
      } else {
        hasStartedRef.current = true;
      }
    }
  }, []);

  useEffect(() => {
    const events = ['click', 'touchstart', 'keydown', 'scroll'];
    events.forEach(event => {
      document.addEventListener(event, startMusic, { passive: true });
    });
    return () => {
      events.forEach(event => {
        document.removeEventListener(event, startMusic);
      });
    };
  }, [startMusic]);

  // Listen for external music toggle events
  useEffect(() => {
    const handleMusicToggle = (e) => {
      const isMuted = e.detail?.muted;
      setMuted(isMuted);
      if (audioRef.current) {
        if (isMuted) {
          audioRef.current.pause();
        } else if (hasStartedRef.current) {
          audioRef.current.play().catch(() => {});
        }
      }
    };
    window.addEventListener('musicToggle', handleMusicToggle);
    return () => window.removeEventListener('musicToggle', handleMusicToggle);
  }, []);

  // Pause when the app goes to background (tab hidden, app backgrounded on
  // mobile, screen locked, etc.) and resume when it becomes visible again —
  // unless the user explicitly muted the music.
  useEffect(() => {
    const handleVisibility = () => {
      if (!audioRef.current || !hasStartedRef.current) return;
      if (document.hidden) {
        audioRef.current.pause();
      } else if (localStorage.getItem('velvet_music_muted') !== 'true') {
        audioRef.current.play().catch(() => {});
      }
    };
    // The 'visibilitychange' event covers both tab switching on desktop and
    // app backgrounding on Android/iOS (TWA, PWA, Safari, Chrome).
    document.addEventListener('visibilitychange', handleVisibility);
    // 'pagehide'/'pageshow' add an extra safety net for iOS Safari which
    // sometimes skips visibilitychange when entering bfcache.
    window.addEventListener('pagehide', handleVisibility);
    window.addEventListener('pageshow', handleVisibility);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('pagehide', handleVisibility);
      window.removeEventListener('pageshow', handleVisibility);
    };
  }, []);

  // Handle track end
  const handleTrackEnd = () => {
    const nextTrack = (currentTrack + 1) % tracks.length;
    setCurrentTrack(nextTrack);
  };

  // When track changes, load and play
  useEffect(() => {
    if (audioRef.current && hasStartedRef.current && !muted) {
      audioRef.current.src = tracks[currentTrack];
      audioRef.current.load();
      audioRef.current.play().catch(() => {});
    }
  }, [currentTrack, muted]);

  return (
    <audio
      ref={audioRef}
      src={tracks[currentTrack]}
      onEnded={handleTrackEnd}
      preload="auto"
    />
  );
}
